/**
 * Ancrages sur le calendrier scolaire
 *
 * Certaines échéances d'un foyer ne se décrivent pas par une RRULE : « payer la
 * cantine au milieu de chaque période de vacances » dépend d'un calendrier
 * officiel qui bouge chaque année et n'a aucune régularité (la Toussaint fait
 * 17 jours, l'été 8 semaines). On les modélise donc comme un *ancrage* sur une
 * liste de périodes, et non comme une récurrence.
 *
 * Les périodes viennent de l'API du calendrier scolaire du ministère, mises en
 * cache en base (voir palier 4). Ce module ne connaît que la liste qu'on lui
 * passe : il reste testable sur une fixture, sans réseau.
 */
import { ajouteJours, deNumeroJour, numeroJour, parties } from './dates.js';
/**
 * Zones de vacances telles que les nomme le jeu de données du ministère.
 * L'orthographe compte : elle sert de filtre dans la requête à l'API.
 */
export const ZONES_VACANCES = [
    'Zone A',
    'Zone B',
    'Zone C',
    'Corse',
    'Guadeloupe',
    'Guyane',
    'Martinique',
    'Mayotte',
    'La Réunion',
    'Saint-Pierre-et-Miquelon',
    'Polynésie',
    'Nouvelle Calédonie',
    'Wallis et Futuna',
];
/**
 * Année scolaire contenant une date, au format du jeu de données
 * (« 2026-2027 »). La bascule est fixée au 1er septembre : en juillet et en
 * août, on est encore dans l'année qui s'achève.
 */
export function anneeScolaireDe(date) {
    const { annee, mois } = parties(date);
    return mois >= 9 ? `${annee}-${annee + 1}` : `${annee - 1}-${annee}`;
}
/** Année scolaire suivante — celle qu'on récupère en juillet. */
export function anneeScolaireSuivante(anneeScolaire) {
    const debut = Number(anneeScolaire.split('-')[0]);
    return `${debut + 1}-${debut + 2}`;
}
export const ANCRAGES = [
    'milieu_vacances',
    'debut_vacances',
    'veille_rentree',
    'semaine_avant_rentree',
];
export const LIBELLE_ANCRAGE = {
    milieu_vacances: 'au milieu de chaque période de vacances',
    debut_vacances: 'au premier jour de chaque période de vacances',
    veille_rentree: 'la veille de chaque rentrée',
    semaine_avant_rentree: 'une semaine avant chaque rentrée',
};
/** Milieu de la période, arrondi vers le bas — déterministe, sans dépendance à l'heure. */
export function milieuDe(periode) {
    const d = numeroJour(periode.debut);
    const f = numeroJour(periode.fin);
    return deNumeroJour(d + Math.floor((f - d) / 2));
}
/** Date d'ancrage d'une période donnée. */
export function dateAncrage(ancrage, periode) {
    switch (ancrage) {
        case 'milieu_vacances':
            return milieuDe(periode);
        case 'debut_vacances':
            return periode.debut;
        case 'veille_rentree':
            return periode.fin;
        case 'semaine_avant_rentree':
            return ajouteJours(periode.fin, -7);
    }
}
function triees(periodes) {
    return [...periodes].sort((a, b) => (a.debut < b.debut ? -1 : a.debut > b.debut ? 1 : 0));
}
/**
 * Prochaine date d'ancrage strictement postérieure à `apres`.
 * Renvoie `null` quand le cache ne va pas assez loin — l'appelant doit alors le
 * dire à l'utilisateur (« calendrier scolaire pas encore récupéré ») plutôt que
 * d'inventer une date.
 */
export function prochainAncrage(ancrage, apres, periodes) {
    const candidats = triees(periodes)
        .map((p) => dateAncrage(ancrage, p))
        .filter((d) => d > apres)
        .sort();
    return candidats[0] ?? null;
}
/** Les `nombre` prochaines dates d'ancrage, pour l'aperçu du sélecteur. */
export function prochainsAncrages(ancrage, apres, periodes, nombre = 3) {
    return triees(periodes)
        .map((p) => dateAncrage(ancrage, p))
        .filter((d) => d > apres)
        .sort()
        .slice(0, nombre);
}
/** Période de vacances contenant `date`, ou `null` si c'est une période scolaire. */
export function periodeDe(date, periodes) {
    return triees(periodes).find((p) => date >= p.debut && date <= p.fin) ?? null;
}
//# sourceMappingURL=vacances.js.map